/**
 * Biometric data types + derived helpers.
 *
 * The series come from the real backend (BE-2 / FEATURE_020): GET
 * `/api/v1/medical/patients/{id}/biometrics`. Per PRD 09 v1.4 / 04 v2.0 the third reference metric is
 * **resting heart rate** (안정시 심박수), NOT BMI/weight — the abnormal band (서맥/빈맥) is decided
 * server-side against the administrator-configured normal range, never on the client.
 */

// ── Types ──

export interface BioStep {
	date: string; // YYYY-MM-DD
	value: number; // steps
	/** Server-supplied daily goal (admin-configured STEP_COUNT normal min). null = no reference row configured. */
	goal: number | null;
}

export interface BioSleep {
	date: string;
	value: number; // total hours
	deep: number;
	light: number;
	rem: number;
	/**
	 * Hours the patient typed in by hand instead of wearing a device. Such a night carries no stage split at
	 * all, so deep/light/rem are 0 for it and this is the only figure that can draw its bar.
	 *
	 * The server also still sends `awake` (waking up mid-night), but nothing on this screen displays it any
	 * more, so it deliberately stops at the wire type and is not carried into the app model — a field kept
	 * here that no component reads would read as something still in use.
	 */
	manual: number;
	/** Server-supplied normal range (admin-configured SLEEP_DURATION). null = no reference row configured. */
	normalMin: number | null;
	normalMax: number | null;
}

/** Server-decided resting-heart-rate band. null = no admin normal range configured (unknown, not normal). */
export type HrBand = "LOW" | "NORMAL" | "HIGH";

export interface BioHeartRate {
	date: string;
	value: number; // resting heart rate, bpm
	band: HrBand | null;
}

/** Period averages the header cards show; each null when that metric has no readings in the period. */
export interface BiometricSummary {
	avgSteps: number | null;
	avgSleepHours: number | null;
	avgRestingHr: number | null;
	hrAbnormalDays: number | null;
}

export interface BiometricData {
	steps: BioStep[];
	sleep: BioSleep[];
	heartRate: BioHeartRate[];
	summary: BiometricSummary;
	lastSync: string | null; // ISO-8601; null when the device never synced
	syncStale: boolean; // BE-authoritative "readings may be out of date" flag
}

// ── Resting-heart-rate band helpers (band is BE-supplied — never derived here) ──

export type HrBandKey = "low" | "normal" | "high";

/** Map the wire band to an i18n key (`patients.biometric.hr.<key>`); null when unknown. */
export function hrBandKey(band: HrBand | null): HrBandKey | null {
	switch (band) {
		case "LOW":
			return "low";
		case "HIGH":
			return "high";
		case "NORMAL":
			return "normal";
		default:
			return null;
	}
}

/** Display color for a band: 서맥 blue · 정상 green · 빈맥 red · unknown gray. */
export function hrBandColor(band: HrBand | null): string {
	switch (band) {
		case "LOW":
			return "#3b82f6";
		case "HIGH":
			return "#ef4444";
		case "NORMAL":
			return "#10b981";
		default:
			return "#6b7280";
	}
}

/** Abnormal = 서맥(LOW) or 빈맥(HIGH); NORMAL and unknown are not flagged. */
export function isHrAbnormal(band: HrBand | null): boolean {
	return band === "LOW" || band === "HIGH";
}

// ── Period helpers ──

export type PeriodKey = "day" | "month1" | "month3" | "all";

/** Period label key (use with i18n `patients.biometric.period.<key>`). */
export function bioPeriodLabel(period: string): PeriodKey {
	switch (period) {
		case "7":
			return "day";
		case "30":
			return "month1";
		case "90":
			return "month3";
		default:
			return "all";
	}
}

/** Slice array to last N days. */
export function sliceFor<T>(arr: T[], days: number): T[] {
	return arr.slice(Math.max(0, arr.length - days));
}

/** One densified chart slot: the calendar day + its reading (null when the device had no data that day). */
export interface DaySlot<T> {
	date: string; // YYYY-MM-DD
	entry: T | null;
}

/** Enumerate YYYY-MM-DD strings from `start` to `end` inclusive (local time). */
function eachDay(start: Date, end: Date): string[] {
	const out: string[] = [];
	const cur = new Date(start);
	while (cur <= end) {
		const y = cur.getFullYear();
		const m = String(cur.getMonth() + 1).padStart(2, "0");
		const d = String(cur.getDate()).padStart(2, "0");
		out.push(`${y}-${m}-${d}`);
		cur.setDate(cur.getDate() + 1);
	}
	return out;
}

/**
 * Return one slot per calendar day for the charts, so missing dates render as gaps
 * instead of collapsing the x-axis (SHCT-565). The BE sends only days that actually
 * have readings; this left-joins that sparse series onto a continuous date window.
 *
 * Window: for 7일 / 30일 / 90일, exactly that many calendar days **ending today**. For 전체, the
 * discharge date (or an older first reading) through today.
 *
 * Anchoring at today rather than at the latest reading is what makes a stalled device visible: the
 * days since it last synced stay on the axis as empty slots. The old anchor ended the axis at the
 * final reading, so a device silent for a week still drew a full-looking chart — which contradicts
 * the header meta PRD 04 §3.3 requires right above it (`마지막 동기화 … (N일 전)`, red past 7 days).
 * The same section is silent on the anchor itself; today was confirmed by the team (2026-08-06).
 *
 * A fixed period is exactly its own length and is NOT clipped at the discharge date. It used to be:
 * the thought was that a patient discharged five days ago should not collect 25 empty slots from
 * before they were enrolled. But then "30일" drew whatever was left of the period — a patient
 * discharged eleven days ago got an eleven-day chart labelled 1개월 — and both PRD 04 §3.3, which
 * names the tabs in days, and SHCT-565's own expected result ("all days in a given period must be
 * rendered, regardless of if that day has data or not") ask for the period as stated. The empty
 * head of the axis is the honest picture: those days really do have no readings. 전체 keeps a start
 * because it needs one, and a reading older than the discharge date widens it rather than being
 * hidden — data is never dropped off the chart.
 */
export function densifyByDay<T extends { date: string }>(
	arr: T[],
	days: number,
	opts: { today: string; floor?: string | null } = { today: todayIso() },
): DaySlot<T>[] {
	const sorted = [...arr].sort((a, b) => a.date.localeCompare(b.date));
	const byDate = new Map(sorted.map((x) => [x.date, x]));

	// The axis ends today, or at the last reading if that somehow runs past today (clock skew).
	const lastReading = sorted[sorted.length - 1]?.date;
	const endIso =
		lastReading && lastReading > opts.today ? lastReading : opts.today;
	const end = new Date(`${endIso}T00:00:00`);

	let start: Date;
	if (days < 9999) {
		start = new Date(end);
		start.setDate(start.getDate() - (days - 1));
	} else {
		// 전체 — the earliest day there is anything to show from: the discharge date, pulled back if a
		// reading predates it. With neither, there is no span to draw.
		const startCandidates = [opts.floor, sorted[0]?.date].filter(
			(d): d is string => typeof d === "string" && d.length > 0,
		);
		if (startCandidates.length === 0) return [];
		start = new Date(`${startCandidates.sort()[0]}T00:00:00`);
		if (end < start) return [];
	}
	return eachDay(start, end).map((d) => ({ date: d, entry: byDate.get(d) ?? null }));
}

/** Today as YYYY-MM-DD in the viewer's timezone — the axis anchor. */
export function todayIso(): string {
	const n = new Date();
	const m = String(n.getMonth() + 1).padStart(2, "0");
	const d = String(n.getDate()).padStart(2, "0");
	return `${n.getFullYear()}-${m}-${d}`;
}
